"use client";

/**
 * OpeningHookScene — Niche v3 Detail Scene 0 (The Summons).
 *
 * Concept: niche is a territory you've just been granted. Not a report,
 * not a problem statement — an investiture. Ornate frame + engraved name +
 * pinned medal (grade) + whisper line (audience claim).
 *
 * Layout:
 *   - Deep navy base + low-opacity mood gradient overlay + noise
 *   - Eyebrow "FILE № {shortId}" uppercase tracked
 *   - OrnateFrame with EngravedText(niche.name) centered inside
 *   - Ribbon + FoilGradeBadge pinned under the frame (medal)
 *   - Whisper: nicherClaim.audience (or first sentence of dreamHook fallback)
 *   - Scroll hint "read on ↓"
 *
 * Animation timeline (seconds from mount):
 *   0.0  ambient bg fade-in
 *   0.2  eyebrow fade-up
 *   0.3  frame starts drawing (OrnateFrame handles internal staging)
 *   0.7  engraved name wipe reveal (900ms)
 *   1.6  ribbon + medal spring-pin
 *   2.1  whisper fade-up
 *   2.8  scroll hint + pulse
 *
 * Data mapping:
 *   niche.name              → EngravedText (inside frame)
 *   niche.score             → FoilGradeBadge grade/label
 *   niche.visualGradient    → frame accent hex + ambient mood gradient
 *   niche.nicherClaim.audience → whisper line (fallback: dreamHook first sentence)
 *   niche.id                → eyebrow short serial + layoutId scaffolding
 *
 * NOT rendered in this scene (moved elsewhere or dropped):
 *   - dreamHook full prose (used only as whisper fallback)
 *   - nicherClaim.twitterBio ("Hi. I'm ..." didn't work)
 *   - pathForward.nineDayHook / first-sale chip → Scene 4
 *   - searchConfidence chip → Scene 5 / dropped
 *   - visualGradient.descriptor → replaced by serial eyebrow
 *
 * Reduced-motion: everything instant; clip-path wipe + scaleX draws skipped.
 */

import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getScoreGrade } from "@/lib/score-grade";
import { getMoodGradient, resolveAccent } from "@/lib/niche-accent-tokens";
import { FoilGradeBadge } from "@/components/shared-primitives/FoilGradeBadge";
import { OrnateFrame } from "../shared/OrnateFrame";
import { EngravedText } from "../shared/EngravedText";
import type { NicheCandidateV3 } from "@/types/niche-v3";

interface OpeningHookSceneProps {
  niche: NicheCandidateV3;
  /** Currently unused — reserved for future meta chip if we reintroduce it. */
  searchConfidence?: number;
}

/** Derive 6-char serial from niche.id; fallback generic. */
function deriveSerial(niche: NicheCandidateV3): string {
  const id = niche.id?.replace(/-/g, "") ?? "";
  if (id.length >= 6) return `FILE № ${id.slice(0, 6).toUpperCase()}`;
  return "FOUNDRY FILE";
}

/**
 * Pick the whisper line — one emotional sentence under the frame.
 * Priority:
 *   1. nicherClaim.audience (always starts "For ..." — identity-flattery)
 *   2. First sentence of dreamHook
 *   3. null (whisper hidden)
 */
function deriveWhisper(niche: NicheCandidateV3): string | null {
  const audience = niche.nicherClaim?.audience?.trim();
  if (audience) return audience;

  const hook = niche.dreamHook?.trim();
  if (!hook) return null;
  const match = hook.match(/^[^.!?]+[.!?]/);
  return match ? match[0].trim() : hook.slice(0, 120);
}

export function OpeningHookScene({ niche }: OpeningHookSceneProps) {
  const reduced = useReducedMotion();

  const gradeInfo = getScoreGrade(niche.score);

  // Shared-layout id scaffolding — must match Grid card derivation. Keeps
  // future SharedLayout transition viable when Grid + Detail share one root.
  const layoutKey =
    niche.id?.trim() ||
    niche.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

  const accentDescriptor = resolveAccent(
    niche.visualGradient?.accent,
    niche.visualGradient?.mood ?? "stable",
  );
  const accentHex = accentDescriptor.hex;

  const moodGradient = niche.visualGradient?.mood
    ? getMoodGradient(niche.visualGradient.mood)
    : "bg-gradient-to-br from-amber-300 to-orange-500";

  const serial = deriveSerial(niche);
  const whisper = deriveWhisper(niche);

  return (
    <section
      id="scene-0-opening-hook"
      aria-labelledby="scene-0-title"
      className={cn(
        "relative flex min-h-[calc(100svh-var(--layout-back-bar-h))] w-full flex-col items-center justify-center overflow-hidden scroll-mt-20",
        "bg-[#0b0f1e] px-4 py-10 sm:min-h-[90vh] sm:px-6 sm:py-16 lg:min-h-screen lg:px-16 lg:py-28",
      )}
    >
      <h1 id="scene-0-title" className="sr-only">
        {niche.name}
      </h1>

      {/* Ambient mood gradient — low opacity, sets emotional temperature */}
      <motion.div
        aria-hidden
        initial={reduced ? { opacity: 0.15 } : { opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ duration: reduced ? 0 : 0.5, ease: "easeOut" }}
        className={cn("pointer-events-none absolute inset-0", moodGradient)}
      />

      {/* Noise texture — subtle film grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* Spotlight — soft radial glow centered where the frame sits.
       * Creates a "museum lighting" sense of depth instead of flat overlay. */}
      <motion.div
        aria-hidden
        initial={reduced ? { opacity: 0.4 } : { opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: reduced ? 0 : 0.9, delay: reduced ? 0 : 0.1 }}
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 55% 45% at 50% 48%, ${accentHex}22 0%, ${accentHex}10 35%, transparent 70%)`,
        }}
      />

      {/* Center column — eyebrow / frame / medal / whisper / scroll */}
      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center">
        {/* Eyebrow — serial "FILE № ..." */}
        <motion.div
          aria-hidden
          initial={reduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduced ? 0 : 0.5,
            delay: reduced ? 0 : 0.2,
            ease: "easeOut",
          }}
          className="mb-5 text-[9px] font-medium uppercase tracking-[0.26em] text-white/35 sm:mb-8 sm:text-[10px] sm:tracking-[0.32em] lg:text-[11px]"
        >
          {serial}
        </motion.div>

        {/* Ornate frame with engraved name */}
        <motion.div
          layoutId={`niche-${layoutKey}-hook`}
          className="relative w-full overflow-hidden"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduced ? 0 : 0.3 }}
        >
          <OrnateFrame accentHex={accentHex} delay={reduced ? 0 : 0.3}>
            <div className="flex min-h-[112px] items-center justify-center sm:min-h-[140px] lg:min-h-[200px]">
              <EngravedText
                text={niche.name}
                delay={reduced ? 0 : 0.7}
                duration={0.9}
                className="text-center leading-[0.95] tracking-tight text-[clamp(26px,6.4vw,76px)] sm:text-[clamp(30px,6.4vw,76px)]"
              />
            </div>
          </OrnateFrame>

          {/* Sheen sweep — one-shot diagonal light pass over frame+name
           * after the engraved wipe finishes. Seals the reveal moment
           * with a "reflection" of light across the nameplate. */}
          {!reduced && (
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 w-[45%] mix-blend-overlay"
              style={{
                background:
                  "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.45) 48%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0.45) 52%, transparent 70%)",
              }}
              initial={{ left: "-50%", opacity: 0 }}
              animate={{ left: ["-50%", "-50%", "105%"], opacity: [0, 0.9, 0] }}
              transition={{
                duration: 1.1,
                delay: 1.9,
                times: [0, 0.05, 1],
                ease: "easeOut",
              }}
            />
          )}
        </motion.div>

        {/* Foil medal — overlaps frame bottom edge so it reads as a seal
         * pressed into the nameplate, not a pendant hanging from it.
         * Deep drop-shadow gives physical depth. */}
        <motion.div
          layoutId={`niche-${layoutKey}-foil`}
          className="relative z-[2] -mt-7 flex flex-col items-center sm:-mt-10 lg:-mt-14 [filter:drop-shadow(0_10px_22px_rgba(0,0,0,0.6))]"
          initial={reduced ? false : { rotate: -6, scale: 0.82, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          transition={
            reduced
              ? { duration: 0 }
              : {
                  type: "spring",
                  stiffness: 200,
                  damping: 16,
                  delay: 1.7,
                }
          }
        >
          <FoilGradeBadge
            grade={gradeInfo.grade}
            label={gradeInfo.label}
            size="md"
            animateOnMount={false}
            className="sm:hidden"
          />
          <FoilGradeBadge
            grade={gradeInfo.grade}
            label={gradeInfo.label}
            size="lg"
            animateOnMount={false}
            className="hidden sm:inline-flex"
          />
        </motion.div>

        {/* Whisper — italic serif one-liner */}
        {whisper && (
          <motion.p
            initial={reduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: reduced ? 0 : 0.55,
              delay: reduced ? 0 : 2.1,
              ease: "easeOut",
            }}
            className={cn(
              "mt-6 max-w-2xl text-center font-heading italic leading-[1.35] sm:mt-8 sm:leading-[1.4]",
              "text-[clamp(16px,2.2vw,26px)] text-white/70 sm:text-[clamp(18px,2.2vw,26px)]",
            )}
          >
            {whisper}
          </motion.p>
        )}

        {/* Scroll indicator */}
        <motion.div
          aria-hidden
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: reduced ? 0 : 2.8 }}
          className="mt-8 flex justify-center sm:mt-14"
        >
          <motion.span
            animate={reduced ? undefined : { y: [0, 6, 0] }}
            transition={
              reduced
                ? undefined
                : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }
            }
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] text-white/30"
          >
            <ChevronDown className="size-3" aria-hidden />
            <span>read on</span>
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
